/**
 * Notification Feedback Utility
 * Shared chime sounds + haptic feedback for the entire ecosystem.
 * Usage: import { feedback } from '../utils/notificationFeedback';
 *        feedback.trade();   // trade chime + haptic
 *        feedback.sale();    // sale chime + haptic
 *        feedback.success(); // generic success
 */

// ─── Haptic Engine ───────────────────────────────────────────
let _hapticModule = null;

const loadHaptic = async () => {
  if (_hapticModule) return _hapticModule;
  try {
    const mod = await import("./nativeBridge");
    _hapticModule = mod.haptic;
    return _hapticModule;
  } catch {
    return null;
  }
};

const vibrate = (pattern) => {
  if ("vibrate" in navigator) navigator.vibrate(pattern);
};

const hapticLight = async () => {
  try { const h = await loadHaptic(); await h?.light(); } catch { vibrate(10); }
};
const hapticMedium = async () => {
  try { const h = await loadHaptic(); await h?.medium(); } catch { vibrate([20, 10, 20]); }
};
const hapticHeavy = async () => {
  try { const h = await loadHaptic(); await h?.heavy(); } catch { vibrate([40, 20, 40]); }
};
const hapticSuccess = async () => {
  try { const h = await loadHaptic(); await h?.success(); } catch { vibrate([10, 30, 10, 30, 10]); }
};

// ─── Sound Engine (Web Audio API) ────────────────────────────
const playTone = (notes, options = {}) => {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    const { volume = 0.12, type = "sine", gap = 0.12, decay = 0.35 } = options;

    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = type;
      osc.frequency.value = freq;
      const start = now + i * gap;
      gain.gain.setValueAtTime(volume, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + decay);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + decay + 0.05);
    });

    const total = notes.length * gap + decay + 200;
    setTimeout(() => ctx.close().catch(() => {}), total);
  } catch (_) {}
};

// ─── Sound Presets ───────────────────────────────────────────
const sounds = {
  /** Two-note ascending chime — trades, offers */
  trade: () => playTone([523.25, 659.25], { volume: 0.14, gap: 0.12 }),

  /** Bright triple arpeggio — sale complete, purchase success */
  sale: () => playTone([523.25, 659.25, 783.99], { volume: 0.13, gap: 0.1, decay: 0.3 }),

  /** Single soft ping — generic notification */
  ping: () => playTone([880], { volume: 0.1, decay: 0.25 }),

  /** Descending two-note — error, rejection */
  error: () => playTone([659.25, 440], { volume: 0.1, gap: 0.15 }),

  /** Quick high blip — button tap, small action */
  tap: () => playTone([1046.5], { volume: 0.06, decay: 0.12 }),

  /** Rising four-note fanfare — achievement, reward, pack rip reveal */
  fanfare: () => playTone([523.25, 659.25, 783.99, 1046.5], { volume: 0.14, gap: 0.1, decay: 0.4 }),

  /** Warm two-note — gift received */
  gift: () => playTone([440, 659.25], { volume: 0.12, gap: 0.15, decay: 0.4 }),
};

// ─── Combined Feedback Presets ───────────────────────────────
export const feedback = {
  /** New trade request / offer received */
  trade: () => { sounds.trade(); hapticMedium(); },

  /** Offer accepted */
  offerAccepted: () => { sounds.sale(); hapticSuccess(); },

  /** Offer rejected / trade cancelled */
  offerRejected: () => { sounds.error(); hapticLight(); },

  /** Card sold / purchase complete */
  sale: () => { sounds.sale(); hapticSuccess(); },

  /** Pack rip reveal moment */
  packRip: () => { sounds.fanfare(); hapticHeavy(); },

  /** Gift received */
  gift: () => { sounds.gift(); hapticMedium(); },

  /** Generic notification (bell icon, new alert) */
  notification: () => { sounds.ping(); hapticLight(); },

  /** Achievement / reward unlocked */
  achievement: () => { sounds.fanfare(); hapticSuccess(); },

  /** Balance top-up / points added */
  topup: () => { sounds.sale(); hapticMedium(); },

  /** Button tap — light tactile */
  tap: () => { sounds.tap(); hapticLight(); },

  /** Listing confirmed */
  listed: () => { sounds.trade(); hapticLight(); },

  /** Error / failure */
  error: () => { sounds.error(); hapticMedium(); },
};

export default feedback;
